import { WebVTT, VTTCue } from 'videojs-vtt.js';
import logger from '../../utils/logger';
import {
  EVENT_CUE_CHANGE,
  EVENT_TRACKS_CHANGE,
  EVENT_TRACK_LOAD,
  EVENT_POSITION
} from '../constants';
import guid from '../utils/guid';
import range from '../utils/range';
import EventEmitter from '../event-emitter';
import isFunction from '../utils/is-function';

/**
 * Maps a position to the closest second, rounded down
 *
 * @param {number} position a position in seconds
 *
 * @return {number}
 */
function map (position) {
  return Math.floor(position);
}

/**
 * Trigger text track events on the player
 *
 * @param {string} type The text track event type
 * @param {object} track The targetted track
 */
function triggerTrackEvent (type, track) {
  let event;

  if (type === EVENT_CUE_CHANGE) {
    event = {
      type: type,
      track: track,
      currentCues: track.activeCues || []
    };
  } else if (type === EVENT_TRACKS_CHANGE) {
    event = {
      type: type,
      tracks: this.textTracks
    };
  } else {
    event = {
      type: type,
      track: track
    };
  }

  this.emit(event);
}

/**
 * Updates the list of active cues on all text tracks
 *
 * @param {number} currentTime The current playback time in seconds
 */
function updateActiveTrackListCues (currentTime) {
  this.textTracks.forEach(track => {
    updateActiveTrackCues.call(this, track, currentTime);
  });
}

/**
 * Updates the list of active cues on a text track
 *
 * @param {TextTrack} track The track to update
 */
function updateActiveTrackCues (track, position) {
  // ignore disabled tracks
  if (track.mode === 'disabled') return;

  // round down to the nearest second
  const second = map(position);

  // resolve a list of cues active at the current position, as well as a new hash for the active items
  const resolved = (track.index.byPosition[second] || []).reduce(function (resolved, cueIndex) {
    const cue = track.cues[cueIndex];

    if (!cue) return resolved;

    // the index may return cues that don't start on the second, so we filter out
    // the ones that started or ended somewhere in the middle of the range
    if (position >= cue.startTime && position < cue.endTime) {
      // add the cue to the list of active cues
      resolved.activeCues.push(cue);

      // update the active cues hash with this cues index on the tracks cues list
      resolved.activeCuesHash = resolved.activeCuesHash
        ? resolved.activeCuesHash + ',' + cueIndex
        : cueIndex.toString();
    }

    return resolved;
  }, {
    activeCues: [],
    activeCuesHash: ''
  });

  // if the new active hash differs from the current one, then it means
  // that one or more cues on the track have changed
  if (track.activeCuesHash !== resolved.activeCuesHash) {
    const previousCues = track.activeCues;

    // call the onexit handler when exiting an active cue
    for (let i = 0, len = previousCues.length; i < len; i++) {
      if (resolved.activeCues.indexOf(previousCues[i]) === -1) {
        previousCues[i].onexit();
      }
    }

    // update the cached copy of the active cues
    track.activeCues = resolved.activeCues;

    // update the cached copy of the active cues hash
    track.activeCuesHash = resolved.activeCuesHash;

    // call the onenter handler when entering a new active cue
    for (let i = 0, len = resolved.activeCues.length; i < len; i++) {
      if (previousCues.indexOf(resolved.activeCues[i]) === -1) {
        resolved.activeCues[i].onenter();
      }
    }

    triggerTrackEvent.call(this, EVENT_CUE_CHANGE, track);
  }
}

/**
 * Loads the data from a remote text track
 *
 * @todo move parsing and cue building to the _loadRemoteTextTrack method, resolving once the cues have been added to the track
 *
 * @param {TextTrack} textTrack The text track
 *
 * @return {Promise} A preomise to return text track data
 */
function loadRemoteTextTrack (textTrack) {
  this.logger.debug(`TextTracks: loading text track data from the remote source '${textTrack.url}'`);

  return new Promise((resolve, reject) => {
    const xhr = new window.XMLHttpRequest();

    xhr.open('GET', textTrack.url, true);

    xhr.onload = _ => {
      this.logger.debug(`TextTracks: text track data has finished loading from the remote source '${textTrack.url}'`);
      resolve(xhr.responseText);
    };

    xhr.onerror = _ => {
      this.logger.debug(`TextTracks: error while loading text track data from the remote source '${textTrack.url}'`);
      reject(new Error('the remote text track failed to load'));
    };

    xhr.ontimeout = _ => {
      this.logger.debug(`TextTracks: loading of text track data timed out for the remote source '${textTrack.url}'`);
      reject(new Error('the remote text track timed out'));
    };

    xhr.onabort = _ => {
      this.logger.debug(`TextTracks: loading of text track data has been aborted for the remote source '${textTrack.url}'`);
      reject(new Error('loading of the remote text track has been aborted'));
    };

    xhr.send();
  });
}

/**
 * Parses VTT data in string format to a list of cues
 *
 * @param {string} vttData The VTT data returned by the remote source
 *
 * @return {Promise} A promise to parse cues from the VTT data
 */
function parseVttData (vttData) {
  this.logger.debug('TextTracks: parsing text track data');

  return new Promise((resolve, reject) => {
    const parser = new WebVTT.Parser(window, WebVTT.StringDecoder());
    const cues = [];

    parser.oncue = (cue) => {
      this.logger.debug('TextTracks: parsed a cue from the remote text track', cue);
      cues.push(cue);
    };

    parser.onflush = _ => {
      this.logger.debug('TextTracks: finished parsing text track data');
      resolve(cues);
    };

    parser.onparsingerror = (e) => {
      this.logger.warn(`TextTracks: failed to parse VTT data: ${e.message}`);
      reject(e);
    };

    // Fixes https://github.com/mozilla/vtt.js/issues/364
    const oldVTTCue = global.VTTCue;

    try {
      global.VTTCue = VTTCue;
      parser.parse(vttData);
      parser.flush();
    } finally {
      global.VTTCue = oldVTTCue;
    }
  });
}

/**
 * Determines whether or not a remote text track should be loaded
 *
 * @param {object} track
 *
 * @return {boolean}
 */
function shouldLoadRemoteTextTrack (track) {
  return (
    (track.url && track.cues.length === 0) &&
    (track.mode === 'showing' || track.mode === 'hidden')
  );
}

/**
 * Adds a text track to the list
 *
 * @param {object|TextTrack} textTrack The track to be added
 *
 * @return {TextTrack}
 */
function addTextTrack (textTrack) {
  // build a text track from props
  if (!(textTrack instanceof TextTrack)) {
    textTrack = this.createTextTrack(textTrack);
  }

  // determine the initial mode of the track
  // always make metadata tracks either hidden or disabled
  if (textTrack.kind === 'metadata' && textTrack.mode !== 'disabled') {
    textTrack.mode = 'hidden';
  } else if (
    (!this.activeTracks[textTrack.kind]) &&
    (textTrack.language === this.options.language)
  ) {
    textTrack.mode = 'showing';
    this.activeTracks[textTrack.kind] = textTrack;
  } else {
    textTrack.mode = 'disabled';
  }

  // add the new track to the list
  this.textTracks.push(textTrack);

  return textTrack;
}

/**
 * Adds a cue to an existing text track
 *
 * @param {TextTrack}           textTrack  The text track to add the cue to
 * @param {TextTrackCue|object} cue        A text track cue object or the properties to create one
 *
 * @return {object} The newly created cue
 */
function addTextTrackCue (textTrack, cue) {
  if (!(cue instanceof TextTrackCue)) {
    cue = this.createTextTrackCue(cue);
  }

  // validate that the end time is greater than the start time
  if (cue.startTime > cue.endTime) {
    this.logger.warn(`Invalid cue on text track '${textTrack.url}': the start time '${cue.startTime}' is greater than the end time '${cue.endTime}'`);
    return;
  }

  // update the cue to innclude a metadata property if the text track is of kind metadata
  if (textTrack.kind === 'metadata') {
    const metadata = JSON.parse(cue.text);

    Object.defineProperties(cue, {
      text: {
        get () { return JSON.stringify(metadata); }
      },
      metadata: {
        get () { return metadata; }
      }
    });
  }

  // add the cue to the track
  textTrack.cues.push(cue);

  // re-index the cues on a track
  textTrack.reindex();

  return cue;
}

/**
 * Replaces a text tracks list of cues
 *
 * @param {TextTrack}            textTrack The text track to add the cues to
 * @param {TextTrack[]|object[]} cues      A list of text track cues or the properties to create one
 */
function setTextTrackCues (textTrack, cues) {
  textTrack.cues = [];
  textTrack.activeCues = [];
  for (let i = 0, len = cues.length; i < len; i++) {
    addTextTrackCue.call(this, textTrack, cues[i]);
  }
}

/**
 * Provides methods for controlling text tracks and their cues
 *
 * @fixme add a track id index to simplify getting a track by it's id - this index will need to be maintained such that when a track is removed, the index must be updated too
 *
 * @todo handle loading and parsing error better
 * @todo validate CORS requests
 */
export default class TextTracksController extends EventEmitter {
  /**
   * Constructor.
   *
   * @param {Player} player The player this controller is instantiated for
   * @param {object} options The options to use when instantiating the controller
   */
  constructor (player, options = {}) {
    super();

    this.player = player;
    this.options = options;
    this.textTracks = [];
    this.logger = options.logger || logger;

    this.activeTracks = {
      captions: null,
      subtitles: null,
      chapters: null
    };

    this.ontimeupdate = this.ontimeupdate.bind(this);

    // wait for the player to be ready before initialising media event bindings
    this.player.on(EVENT_POSITION, this.ontimeupdate);
  }

  /**
   * Update the active cues on a text track when the playback position changes
   */
  ontimeupdate (event) {
    updateActiveTrackListCues.call(this, event.position);
  }

  /**
   * Adds a new text track to the list
   *
   * @fixme allow overriding of the text track id, if the id exists, replace or error
   *
   * @param {TextTrack|object} textTrack A text track object or the props to build a text track
   *
   * @return {object} A text track object
   */
  addTextTrack (textTrack) {
    textTrack = addTextTrack.call(this, textTrack);

    this.logger.debug(`TextTracks: added text track '${textTrack.id}'`);

    // load the remote text track if needed
    if (shouldLoadRemoteTextTrack.call(this, textTrack)) {
      const _loadRemoteTextTrackSuccess = (result) => {
        triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
      };

      this.loadRemoteTextTrack(textTrack)
        .then(_loadRemoteTextTrackSuccess);
    } else {
      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
    }

    return textTrack;
  }

  /**
   * Create a text track
   *
   * @param {object} props The text track properties
   *
   * @return {TextTrack}
   */
  createTextTrack (props) {
    return new TextTrack(props);
  }

  /**
   * Create a text track cue
   *
   * @param {object} props The text track cue properties
   *
   * @return {TextTrackCue}
   */
  createTextTrackCue (props) {
    return new TextTrackCue(props);
  }

  /**
   * Remove all text tracks from the list
   */
  clearTextTracks () {
    if (this.textTracks.length > 0) {
      this.logger.debug(`TextTracks: Clearing ${this.textTracks.length} text tracks`);

      this.textTracks = [];

      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
    }
  }

  /**
   * Removes a text track from the list
   *
   * @param {TextTrack} trackId The id of the track to be removed
   *
   * @return {object} The text track that got removed
   */
  removeTextTrack (textTrack) {
    const trackIndex = this.textTracks.indexOf(textTrack);

    if (trackIndex !== -1) {
      this.logger.debug(`TextTracks: removing text track '${textTrack.id}'`);

      this.textTracks.splice(trackIndex, 1);

      // inform the world that the list of text tracks has been updated
      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
    }
  }

  /**
   * Retrieve a text track by its id
   *
   * @param {string} trackId The id of the text track to find
   *
   * @return {object} The text track with a matching id
   */
  getTextTrackById (trackId) {
    for (let i = 0, l = this.textTracks.length; i < l; i++) {
      if (this.textTracks[i].id === trackId) {
        return this.textTracks[i];
      }
    }
  }

  /**
   * Change a text tracks mode, modes may only be one of:
   *
   *  - `showing` to enable text track events and also hints that it needs to update when the tracks active cues change
   *  - `hidden` to enable text track events without hinting to the ui that it needs to update when the tracks active cues change
   *  - `disabled` to disable text track events on the track
   *
   * @param {TextTrack} textTrack The text track to have its mode changed
   * @param {string}    mode      The updated mode of the text track
   *
   * @return {TextTrack} The updated text track
   */
  setTextTrackMode (textTrack, mode) {
    // update the track mode as long as it differs
    if (textTrack && textTrack.mode !== mode) {
      this.logger.debug(`TextTracks: updating track '${textTrack.id}' mode to '${mode}'`);

      textTrack.mode = mode;

      if (mode === 'showing') {
        if (this.activeTracks[textTrack.kind]) {
          this.setTextTrackMode(this.activeTracks[textTrack.kind], 'disabled');
        }
        this.activeTracks[textTrack.kind] = textTrack;
      } else if (this.activeTracks[textTrack.kind] === textTrack) {
        this.activeTracks[textTrack.kind] = null;
      }

      const loaded = _ => {
        // let everyone know that something about the track list has changed
        triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);

        // update the list of active cues on this track as long as the mode allows it
        updateActiveTrackCues.call(this, textTrack, this.player.getPosition());
      };

      // load remote text tracks that haven't been loaded yet
      if (shouldLoadRemoteTextTrack.call(this, textTrack)) {
        this.loadRemoteTextTrack(textTrack).then(loaded);
      } else {
        loaded();
      }
    }

    return textTrack;
  }

  /**
   * Loads a remote text track
   *
   * @param {TextTrack} textTrack The remote text track to be loaded
   */
  loadRemoteTextTrack (textTrack) {
    // begin loading the remote text track
    return loadRemoteTextTrack.call(this, textTrack)
      // parse the text track data
      .then(parseVttData.bind(this))
      // add cues parsed from the remote data to the text track
      .then(cues => {
        setTextTrackCues.call(this, textTrack, cues);
        triggerTrackEvent.call(this, EVENT_TRACK_LOAD, textTrack);
      })
      // trigger errors during the load process on the player, but don't interrupt playback
      .catch(error => {
        this.logger.logException(error);
      });
  }

  /**
   * Retrieves a cue from its text track by id
   *
   * @param {TextTrack} textTrack The text track containing the cue
   * @param {string}    cueId     The id of the cue to find
   *
   * @return {TextTrackCue} The cue found on the text track
   */
  getTextTrackCueById (textTrack, cueId) {
    let cue;

    for (let i = 0, l = textTrack.cues.length; i < l; i++) {
      if (textTrack.cues[i].id === cueId) {
        cue = textTrack.cues[i];

        break;
      }
    }

    return cue;
  }

  /**
   * Formats a cue to HTML DOM nodes
   *
   * @param {object} cue The cue to format
   *
   * @return {HTMLDivElement}
   */
  getTextTrackCueAsHTML (cue) {
    return WebVTT.convertCueToDOMTree(window, cue.text);
  }

  /**
   * Adds a cue to an existing text track
   *
   * @param {TextTrack}           textTrack  The text track to add the cue to
   * @param {TextTrackCue|object} cue        A text track cue object or the properties to create one
   *
   * @return {object} The newly created cue
   */
  addTextTrackCue (textTrack, cue) {
    if (textTrack) {
      cue = addTextTrackCue.call(this, textTrack, cue);

      this.logger.debug(`TextTracks: added cue to track '${textTrack.id}'`);

      // trigger the track change event to inform the world that this track has changed
      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);

      // update the active cues on this track, if the added cue is active this should in turn trigger the cue change
      updateActiveTrackCues.call(this, textTrack, this.player.getPosition());

      return cue;
    }
  }

  /**
   * Remove all cues from a text track
   *
   * @param {TextTrack} textTrack The text track id
   */
  clearTextTrackCues (textTrack) {
    if (textTrack) {
      this.logger.debug(`TextTracks: clearing all cues from the track '${textTrack.id}'`);

      const hasActivCues = textTrack.activeCues.length > 0;

      // remove all the cues from the track
      textTrack.cues = [];

      // remove all active cues from the track
      textTrack.activeCues = [];

      // the list
      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);

      // re-index the text track index
      textTrack.reindex();

      // trigger the cue change event if the track had active cues
      if (hasActivCues) {
        triggerTrackEvent.call(this, EVENT_CUE_CHANGE, textTrack);
      }
    }
  }

  /**
   * Remove a cue from a text track
   *
   * @param {TextTrack} textTrack The text track the cue should be removed from
   * @param {string} cueId The text track cue id
   *
   * @return {object} The cue that was removed
   */
  removeTextTrackCue (textTrack, cueId) {
    const cue = this.getTextTrackCueById(textTrack, cueId);

    if (textTrack && cue) {
      this.logger.debug(`TextTracks: removing cue '${cueId}' from the track '${textTrack.id}'`);

      // determine the index of the cue in the cues list
      const cueIndex = textTrack.cues.indexOf(cue);

      // remove the cue from the list
      textTrack.cues.splice(cueIndex, 1);

      // re-index the text track
      textTrack.reindex();

      // the list of text tracks has been changed
      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);

      // update the active cues on this track, if the added cue is active this should in turn trigger the cue change
      updateActiveTrackCues.call(this, textTrack, this.player.getPosition());
    }
  }

  /**
   * Retrieve the list of text tracks
   *
   * @return {array}
   */
  getTextTracks () {
    return this.textTracks;
  }

  /**
   * Retrieve the cues active at a position
   *
   * @param {TextTrack} textTrack
   * @param {number} position
   *
   * @return {array}
   */
  getTextTrackCuesAtPosition (textTrack, position) {
    return (textTrack.index.byPosition[map(position)] || [])
      .reduce(function (cues, index) {
        const cue = textTrack.cues[index];

        // the index may return cues that don't start on the second, so we filter out
        // the ones that started or ended somewhere in the middle of the range
        if (cue && position >= cue.startTime && position <= cue.endTime) {
          // add the cue to the list of active cues
          cues.push(cue);
        }

        return cues;
      }, []);
  }

  /**
   * Adds multiple text tracks at once
   *
   * @todo update setTextTracks to add all tracks before emitting any events
   *
   * @param {object[]} tracks A list of objects containing text track data
   */
  setTextTracks (tracks) {
    // clear existing text tracks
    this.textTracks = [];
    this.activeTracks = {
      captions: null,
      subtitles: null,
      chapters: null
    };

    const promises = [];

    // add each text track to the list
    for (let i = 0, len = tracks.length; i < len; i++) {
      const textTrack = addTextTrack.call(this, tracks[i]);

      // if the track has a src property begin loading and parsing the vtt data from a remote source
      if (shouldLoadRemoteTextTrack.call(this, textTrack)) {
        promises.push(this.loadRemoteTextTrack(textTrack));
      } else if (!textTrack.url) {
        triggerTrackEvent.call(this, EVENT_TRACK_LOAD, textTrack);
      }
    }

    // if none of the tracks are remote and needed to be loaded, then emit the tracks change event immediately
    if (promises.length === 0) {
      return triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
    }

    const _setTextTracksSuccess = (results) => {
      triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
      updateActiveTrackListCues.call(this, this.player.getPosition());
    };

    const _setTextTracksError = (error) => {
      this.logger.warn(`TextTracks: Failed to load remote text tracks: ${error.message}`);
    };

    // otherwise wait for all remote text tracks to load before firing the texttrackschange event
    Promise.all(promises).then(_setTextTracksSuccess, _setTextTracksError);
  }

  /**
   * Updates the cues on a text track
   *
   * @todo update setTextTrackCues to clear and add all cues before emitting any events
   *
   * @param {TextTrack} textTrack The text track to be updated
   * @param {object[]}  cues      An array of objects used to build a cue
   */
  setTextTrackCues (textTrack, cues) {
    setTextTrackCues.call(this, textTrack, cues);
    triggerTrackEvent.call(this, EVENT_TRACKS_CHANGE);
    updateActiveTrackListCues.call(this, textTrack);
  }

  /**
   * Destroy the text track controller
   */
  destroy () {
    this.logger.debug('TextTracks: destroying all text tracks');

    this.clearTextTracks();
    this.player.off(EVENT_POSITION, this.ontimeupdate);
  }
}

/**
 * A text track object
 */
export class TextTrack {
  constructor (props) {
    this.cues = [];
    this.activeCues = [];
    this.id = props.id || guid('texttracks');
    this.label = props.label;
    this.kind = props.kind || 'metadata';
    this.mode = this.kind === 'metadata' ? 'hidden' : 'disabled';
    this.language = props.language || 'en';

    if (props.src) {
      this.url = props.src;
    }

    if (props.url) {
      this.url = props.url;
    }

    this.activeCuesHash = '';
    this.index = {
      byPosition: {}
    };
  }

  /**
   * Re-indexes the cues on this track by position
   */
  reindex () {
    const index = this.index;
    const cues = this.cues;

    const startIndex = 0;
    const endIndex = cues.length;

    // create a lookup table to hold our indexes
    const lookups = {};

    // build the lookup table from the start index of the cues list to the end index
    for (let i = startIndex; i < endIndex; i++) {
      const cue = cues[i];

      if (cue) {
        // determine the range of seconds this cue is active for
        const seconds = range(map(cue.startTime), map(cue.endTime));

        // iterate over each active second in the range and add the cue to the index
        for (let s = 0, t = seconds.length; s < t; s++) {
          const second = seconds[s];

          // create the index in the lookup table if it hasn't been created yet
          if (!lookups[second]) {
            lookups[second] = [];
          }

          // push the cue on to the index, indexed by the current second
          lookups[second].push(i);
        }
      }
    }

    // replace the indexes in the inedx object that have changed
    Object.keys(lookups).forEach(function (second) {
      index.byPosition[second] = lookups[second];
    });
  }
}

/**
 * A text track cue object
 */
export class TextTrackCue {
  constructor (props) {
    this.id = props.id || guid('texttrackcues');
    this.startTime = props.startTime;
    this.endTime = props.endTime;
    this.text = props.text;

    if (isFunction(props.onenter)) {
      this.onenter = props.onenter;
    }

    if (isFunction(props.onexit)) {
      this.onexit = props.onexit;
    }
  }

  onenter () {}

  onexit () {}
}

export { parseVttData, loadRemoteTextTrack };
